import { Link } from "@tanstack/react-router";
import {
  type FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { FiArrowLeft } from "react-icons/fi";
import {
  AnnounceRequestError,
  type PublicAnnouncement,
} from "@/features/announce/api/announceClient";
import {
  AnnouncementLevelBadge,
  AnnouncementTimestamps,
} from "@/features/announce/components/AnnouncementContent";
import { useAnnouncementStatus } from "@/features/announce/hooks/useAnnouncementStatus";
import {
  useAnnouncement,
  useDesktopModalAnnouncements,
} from "@/features/announce/hooks/useAnnouncements";
import {
  areAllFetchedAnnouncementsRead,
  getAnnouncementReadThroughTimestamp,
  getFetchedAnnouncementsReadThroughRevision,
} from "@/features/announce/lib/announcementReadReceipt";
import { formatAnnounceHtmlAsPlainText } from "@/features/announce/lib/stripAnnounceHtml";
import { LoadingScreen } from "@/shared/ui/feedback";
import { Button } from "@/shared/ui/form";
import { Modal } from "@/shared/ui/overlay/Modal";

interface DesktopAnnouncementModalProps {
  initialAnnouncementId?: number;
  isOpen: boolean;
  onClose: () => void;
}

export const DesktopAnnouncementModal: FunctionComponent<
  DesktopAnnouncementModalProps
> = ({
  initialAnnouncementId,
  isOpen,
  onClose,
}: DesktopAnnouncementModalProps) => {
  const query = useDesktopModalAnnouncements();
  const hasCurrentRevisionList = query.dataUpdatedAt > 0;
  const announcements = query.data?.announcements;
  const listedInitialAnnouncement = hasCurrentRevisionList
    ? announcements?.find(
        (announcement) => announcement.id === initialAnnouncementId,
      )
    : undefined;
  const hasSettledCurrentRevisionList = hasCurrentRevisionList || query.isError;
  const shouldFetchInitialAnnouncement =
    isOpen &&
    initialAnnouncementId !== undefined &&
    listedInitialAnnouncement === undefined &&
    hasSettledCurrentRevisionList;
  const initialAnnouncementQuery = useAnnouncement(
    shouldFetchInitialAnnouncement ? initialAnnouncementId : null,
  );
  const currentInitialAnnouncement =
    initialAnnouncementQuery.dataUpdatedAt > 0
      ? initialAnnouncementQuery.data
      : undefined;
  const isInitialAnnouncementLoading =
    initialAnnouncementId !== undefined &&
    (!hasSettledCurrentRevisionList ||
      (shouldFetchInitialAnnouncement && initialAnnouncementQuery.isFetching));

  return (
    <DesktopAnnouncementModalView
      announcements={announcements}
      error={query.error}
      initialAnnouncement={currentInitialAnnouncement}
      initialAnnouncementError={initialAnnouncementQuery.error}
      initialAnnouncementId={initialAnnouncementId}
      isInitialAnnouncementLoading={isInitialAnnouncementLoading}
      isLoading={query.isLoading}
      isOpen={isOpen}
      listedInitialAnnouncement={listedInitialAnnouncement}
      onClose={onClose}
      onRetry={(): void => {
        void query.refetch();
      }}
      onRetryInitialAnnouncement={(): void => {
        void initialAnnouncementQuery.refetch();
      }}
      readThroughRevision={
        hasCurrentRevisionList && query.data
          ? getFetchedAnnouncementsReadThroughRevision(
              query.data.announcements,
              query.data.articleUnreadRevision,
            )
          : null
      }
    />
  );
};

interface DesktopAnnouncementModalViewProps
  extends DesktopAnnouncementModalProps {
  announcements: PublicAnnouncement[] | undefined;
  error: unknown;
  initialAnnouncement: PublicAnnouncement | undefined;
  initialAnnouncementError: unknown;
  isInitialAnnouncementLoading: boolean;
  isLoading: boolean;
  listedInitialAnnouncement: PublicAnnouncement | undefined;
  onRetry: () => void;
  onRetryInitialAnnouncement: () => void;
  readThroughRevision: number | null;
}

type ModalView =
  | { name: "list"; focusAnnouncementId?: number }
  | { name: "detail"; announcement: PublicAnnouncement }
  | { name: "initialDetailError"; error: unknown };

const DesktopAnnouncementModalView: FunctionComponent<
  DesktopAnnouncementModalViewProps
> = ({
  announcements,
  error,
  initialAnnouncement,
  initialAnnouncementError,
  initialAnnouncementId,
  isInitialAnnouncementLoading,
  isLoading,
  isOpen,
  listedInitialAnnouncement,
  onClose,
  onRetry,
  onRetryInitialAnnouncement,
  readThroughRevision,
}: DesktopAnnouncementModalViewProps) => {
  const [view, setView] = useState<ModalView>({ name: "list" });
  const handledInitialAnnouncementIdRef = useRef<number | null>(null);
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const announcementButtonRefs = useRef(new Map<number, HTMLButtonElement>());
  const {
    isAnnouncementRead,
    markAnnouncementRead,
    markAnnouncementsReadThrough,
  } = useAnnouncementStatus();

  useEffect(() => {
    if (
      !isOpen ||
      view.name !== "list" ||
      announcements?.length !== 0 ||
      readThroughRevision === null
    ) {
      return;
    }
    markAnnouncementsReadThrough(null, readThroughRevision);
  }, [
    announcements,
    isOpen,
    markAnnouncementsReadThrough,
    readThroughRevision,
    view.name,
  ]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      if (view.name === "detail") {
        backButtonRef.current?.focus();
        return;
      }
      if (view.name === "list" && view.focusAnnouncementId !== undefined) {
        announcementButtonRefs.current.get(view.focusAnnouncementId)?.focus();
      }
    });
    return (): void => cancelAnimationFrame(frame);
  }, [view]);

  const closeModal = (): void => {
    setView({ name: "list" });
    onClose();
  };

  const openDetail = useCallback(
    (announcement: PublicAnnouncement): void => {
      const allFetchedAnnouncementsRead = areAllFetchedAnnouncementsRead(
        announcements,
        announcement.id,
        isAnnouncementRead,
      );

      markAnnouncementRead(announcement);
      if (allFetchedAnnouncementsRead) {
        markAnnouncementsReadThrough(
          getAnnouncementReadThroughTimestamp(
            announcements ?? [],
            announcement,
          ),
          readThroughRevision === null
            ? null
            : Math.max(
                readThroughRevision,
                announcement.article_unread_revision ?? 0,
              ),
        );
      }
      setView({ name: "detail", announcement });
    },
    [
      announcements,
      isAnnouncementRead,
      markAnnouncementRead,
      markAnnouncementsReadThrough,
      readThroughRevision,
    ],
  );

  useEffect(() => {
    if (
      initialAnnouncementId === undefined ||
      handledInitialAnnouncementIdRef.current === initialAnnouncementId
    ) {
      return;
    }
    if (isInitialAnnouncementLoading) return;

    if (
      initialAnnouncementError instanceof AnnounceRequestError &&
      initialAnnouncementError.kind === "not_found"
    ) {
      handledInitialAnnouncementIdRef.current = initialAnnouncementId;
      setView({ name: "list" });
      return;
    }
    if (initialAnnouncement) {
      handledInitialAnnouncementIdRef.current = initialAnnouncementId;
      openDetail(initialAnnouncement);
      return;
    }
    if (listedInitialAnnouncement) {
      handledInitialAnnouncementIdRef.current = initialAnnouncementId;
      openDetail(listedInitialAnnouncement);
      return;
    }
    if (initialAnnouncementError) {
      setView((current) =>
        current.name === "initialDetailError" &&
        current.error === initialAnnouncementError
          ? current
          : { name: "initialDetailError", error: initialAnnouncementError },
      );
      return;
    }
    setView({ name: "list" });
  }, [
    initialAnnouncement,
    initialAnnouncementError,
    initialAnnouncementId,
    isInitialAnnouncementLoading,
    listedInitialAnnouncement,
    openDetail,
  ]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeModal}
      position="top"
      title="告知"
      headerActions={
        <Link
          to="/announcements"
          onClick={closeModal}
          className="inline-flex min-h-11 items-center px-2 text-sm text-primary underline"
        >
          一覧ページ
        </Link>
      }
    >
      {view.name === "detail" ? (
        <AnnouncementModalDetail
          announcement={view.announcement}
          backButtonRef={backButtonRef}
          onBack={(): void =>
            setView({
              name: "list",
              focusAnnouncementId: view.announcement.id,
            })
          }
          onNavigate={closeModal}
        />
      ) : view.name === "initialDetailError" ? (
        <AnnouncementModalError
          error={view.error}
          onRetry={(): void => {
            setView({ name: "list" });
            onRetryInitialAnnouncement();
          }}
        />
      ) : (
        <AnnouncementModalList
          announcements={announcements}
          announcementButtonRefs={announcementButtonRefs.current}
          error={error}
          isAnnouncementRead={isAnnouncementRead}
          isLoading={
            isLoading ||
            (initialAnnouncementId !== undefined &&
              isInitialAnnouncementLoading)
          }
          onOpenDetail={openDetail}
          onRetry={onRetry}
        />
      )}
    </Modal>
  );
};

interface AnnouncementModalListProps {
  announcements: PublicAnnouncement[] | undefined;
  announcementButtonRefs: Map<number, HTMLButtonElement>;
  error: unknown;
  isAnnouncementRead: (
    announcement: Pick<PublicAnnouncement, "id" | "published_at">,
  ) => boolean;
  isLoading: boolean;
  onOpenDetail: (announcement: PublicAnnouncement) => void;
  onRetry: () => void;
}

const AnnouncementModalList: FunctionComponent<AnnouncementModalListProps> = ({
  announcements,
  announcementButtonRefs,
  error,
  isAnnouncementRead,
  isLoading,
  onOpenDetail,
  onRetry,
}: AnnouncementModalListProps) => {
  if (isLoading && announcements === undefined) {
    return <LoadingScreen message="告知を読み込み中..." />;
  }
  if (error && announcements === undefined) {
    return <AnnouncementModalError error={error} onRetry={onRetry} />;
  }
  if (announcements?.length === 0) {
    return (
      <p className="px-4 py-12 text-center text-muted-foreground">
        現在公開されている告知はありません。
      </p>
    );
  }

  return (
    <ol className="space-y-2 p-4">
      {announcements?.map((announcement) => {
        const isRead = isAnnouncementRead(announcement);
        return (
          <li key={announcement.id}>
            <article className="rounded-lg border border-border">
              <button
                ref={(element: HTMLButtonElement | null): void => {
                  if (element)
                    announcementButtonRefs.set(announcement.id, element);
                  else announcementButtonRefs.delete(announcement.id);
                }}
                type="button"
                onClick={(): void => onOpenDetail(announcement)}
                className="grid min-h-11 w-full grid-cols-[minmax(0,1fr)_auto_auto] items-start gap-3 rounded-t-lg p-3 text-left transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                <b className="min-w-0 break-words text-foreground">
                  {announcement.title}
                </b>
                {!isRead && (
                  <strong className="rounded-full bg-primary px-2 py-0.5 text-xs font-bold text-primary-foreground">
                    NEW
                  </strong>
                )}
                <AnnouncementLevelBadge level={announcement.level} />
              </button>
              <footer className="border-t border-border px-3 py-2">
                <AnnouncementTimestamps announcement={announcement} />
              </footer>
            </article>
          </li>
        );
      })}
    </ol>
  );
};

interface AnnouncementModalDetailProps {
  announcement: PublicAnnouncement;
  backButtonRef: React.RefObject<HTMLButtonElement | null>;
  onBack: () => void;
  onNavigate: () => void;
}

const AnnouncementModalDetail: FunctionComponent<
  AnnouncementModalDetailProps
> = ({
  announcement,
  backButtonRef,
  onBack,
  onNavigate,
}: AnnouncementModalDetailProps) => (
  <article className="p-4">
    <button
      ref={backButtonRef}
      type="button"
      onClick={onBack}
      className="mb-4 inline-flex min-h-11 items-center gap-2 text-primary underline"
    >
      <FiArrowLeft aria-hidden="true" />
      告知一覧に戻る
    </button>
    <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border pb-4">
      <h3 className="min-w-0 flex-1 break-words text-xl font-bold text-foreground">
        {announcement.title}
      </h3>
      <AnnouncementLevelBadge level={announcement.level} />
      <AnnouncementTimestamps
        announcement={announcement}
        className="basis-full"
      />
    </header>
    <section aria-labelledby="announcement-modal-body-title" className="py-5">
      <h4 id="announcement-modal-body-title" className="sr-only">
        告知本文
      </h4>
      <p className="whitespace-pre-wrap break-words leading-relaxed text-foreground">
        {formatAnnounceHtmlAsPlainText(announcement.body_html)}
      </p>
    </section>
    <Link
      to="/announcements/$announcementId"
      params={{ announcementId: String(announcement.id) }}
      onClick={onNavigate}
      className="inline-flex min-h-11 items-center text-primary underline"
    >
      この告知をページで開く
    </Link>
  </article>
);

interface AnnouncementModalErrorProps {
  error: unknown;
  onRetry: () => void;
}

const AnnouncementModalError: FunctionComponent<
  AnnouncementModalErrorProps
> = ({ error, onRetry }: AnnouncementModalErrorProps) => {
  const message =
    error instanceof AnnounceRequestError && error.kind === "timeout"
      ? "告知の読み込みが時間内に完了しませんでした。"
      : "告知を読み込めませんでした。通信状態を確認してください。";
  return (
    <section
      aria-label="告知の読み込みエラー"
      role="alert"
      className="flex min-h-[200px] flex-col items-center justify-center gap-4 px-4 text-center text-destructive"
    >
      <p>
        {message}
        <br />
        しばらくしてから再試行してください。
      </p>
      <Button onClick={onRetry}>再試行</Button>
    </section>
  );
};
